/**
 * Table of contents component that detects the H2 headings on the page
 * and creates a list of links to them, detecting which one is currently
 * in view and highlighting it.
 *
 * Currently used/designed for the blog, guides, and lessons pages.
 */

import React, {useState, useEffect, useRef} from 'react';
import {throttle, findLastIndex} from 'lodash';
import {ListBulletIcon} from '@heroicons/react-v2/24/outline';

import {ChevronIcon} from './svg';
import {cx} from '../util';

export const generateTOC = (container, selector = 'h2') => {
  if (!container) return [];
  const elements = container.querySelectorAll(selector);
  const sections = [];
  elements.forEach((el) => {
    sections.push({id: el.id, text: el.textContent});
  });
  return sections;
};

const TableOfContents = ({
  sections,
  mobile,
  defaultOpen = false,
  scrollContainer = null,
  header,
  className, // specify height e.g. h-80 for overflow logic to work
  fallbackTargetId,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [active, setActive] = useState(undefined);
  const tocRef = useRef(null);

  // When ToC is a fixed height with overflow, scroll active item into view upon change
  useEffect(() => {
    if (mobile || !tocRef.current || !active) return;
    const activeEl = tocRef.current.querySelector('[aria-current="location"]');
    if (activeEl) activeEl.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }, [active, mobile]);

  const onClick = (e, section) => {
    e.preventDefault();
    const el = section.locked ? null : document.getElementById(section.id);
    const fallbackTarget = fallbackTargetId ? document.getElementById(fallbackTargetId) : null;
    const target = el || fallbackTarget;
    if (!target) return;
    const sc = scrollContainer ?? window;
    const containerTop = sc === window
      ? sc.scrollY // window doesn't have scrollTop
      : sc.scrollTop;
    const top = target.getBoundingClientRect().top + containerTop - 80;
    sc.scrollTo({top, behavior: 'smooth'});
  };

  useEffect(() => {
    const sc = scrollContainer ?? window;
    const handleScroll = throttle(() => {
      const activeIndex = findLastIndex(sections, (section) => {
        const el = document.getElementById(section.id);
        return el?.getBoundingClientRect().top < 200;
      });
      const isNearBottom = Math.abs(sc.scrollHeight
        - sc.scrollTop
        - sc.offsetHeight) < 5; // 5px tolerance
      setActive(isNearBottom
        ? sections[sections.length - 1]?.id
        : sections[activeIndex]?.id);
    }, 100);

    sc.addEventListener('scroll', handleScroll);
    return () => sc.removeEventListener('scroll', handleScroll);
  }, [sections, scrollContainer]);

  if (!sections || sections.length === 0) return null;

  const defaultMobileHeader = 'Article contents';

  return (
    <aside className={cx('relative w-full mt-4 md:mt-0 bg-white rounded-lg p-4 md:p-2', {
      'shadow border border-gray-100 overflow-hidden': mobile,
    })}
    >
      <nav className="lg:pl-2" aria-label={header ?? defaultMobileHeader}>
        {mobile ? (
          <button
            className="w-full flex justify-between"
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls="table-of-contents-list"
          >
            <span className="flex items-center text-base text-gray-800 font-medium tracking-tight">
              <ListBulletIcon className="w-6 h-6 mr-2 text-gray-600" aria-hidden="true" />
              {header ?? defaultMobileHeader}
            </span>
            <ChevronIcon className="text-gray-500" aria-hidden="true" />
          </button>
        ) : (
          header && (
            <h2 className="flex items-center text-base text-gray-800 font-medium tracking-tight">
              {header}
            </h2>
          )
        )}
        {(open || !mobile) && (
          <div
            ref={!mobile ? tocRef : undefined}
            className={cx(
              {'pt-2': mobile},
              !mobile && ['overflow-y-auto scrollbar-hidden pt-0', className],
            )}
          >
            <ul
              id="table-of-contents-list"
              className={cx('mt-2 leading-snug text-sm', {'space-y-2': mobile})}
            >
              {sections?.map((section) => (
                <li
                  className={cx({
                    truncate: mobile,
                    'text-gray-500 hover:text-gray-700 border-l-2 md:pl-1.5 border-transparent': mobile && !section.locked,
                    'text-indigo-600 hover:text-indigo-600 font-medium border-indigo-600': mobile && active === section.id,
                  })}
                  key={section.id || section.text}
                >
                  <a
                    className={cx(
                      mobile ? '' : 'block px-3 py-2 rounded-lg text-gray-500 hover:text-gray-700',
                      !mobile && active === section.id ? 'bg-gray-100 text-gray-700 font-medium' : '',
                    )}
                    href={`#${section.id}`}
                    onClick={(e) => onClick(e, section)}
                    aria-current={active === section.id ? 'location' : undefined}
                  >
                    {section.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </aside>
  );
};

export default TableOfContents;
